import DateFormatEnum from "constants/DateFormatEnum";
import IconNameEnum from "constants/IconNameEnum";
import moment from "moment";
import * as React from "react";
import { Form, InputGroup } from "react-bootstrap";
import FormatUtility from "utilities/FormatUtility";
import BaseIcon from "./BaseIcon";
import InfoIcon from "./InfoIcon";
import RequiredIcon from "./RequiredIcon";

interface IProps {
    controlId: string;
    id?: string;
    label?: string;
    prepend?: JSX.Element;
    append?: JSX.Element;
    helper?: JSX.Element;
    className?: string;
    type: string;
    name: string;
    placeholder?: string;
    min?: number | Date;
    max?: number | Date;
    minLength?: number;
    maxLength?: number;
    step?: number;
    value: string | number | Date | boolean;
    required: boolean;
    infoCode?: string;
    onFocusHandler?: (name: string, value: any) => void;
    onChangeHandler?: (name: string, value: any) => void;
    onBlurHandler?: (name: string, value: any) => void;
    isInvalid: boolean;
    error?: string;
    customFormat?:
        | "numberWithSeparators"
        | "noNumbers"
        | "accented"
        | "uppercaseAndNumbers"
        | "numbers"
        | "streetNumber"
        | "noSpecials"
        | "phoneNumber";
    formatOnDigit?: boolean;
    disabled?: boolean;
    autocomplete?: string;
    floatingLabel?: boolean;
    fullWidth?: boolean;
    inputClassName?: string;
}
interface IState {
    inputType: string;
    cursorPos: number;
    readonly autocomplete: string;
}

class BaseInput extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            inputType: this.props.type,
            cursorPos: 0,
            autocomplete: this.props.autocomplete ?? "off",
        } as IState;
    }

    private hasUnmounted = false;
    private inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    public componentDidUpdate(): void {
        if (this.props.formatOnDigit && this.props.value != "") {
            let cursorPos = this.state.cursorPos;
            if (this.inputRef.current) {
                if (this.inputRef.current.selectionStart !== cursorPos) {
                    this.inputRef.current.selectionStart = cursorPos;
                }
                if (this.inputRef.current.selectionEnd !== cursorPos) {
                    this.inputRef.current.selectionEnd = cursorPos;
                }
            }
        }
    }

    public componentWillUnmount() {
        this.hasUnmounted = true;
    }

    private onFocusHandler = (evt: React.FocusEvent<HTMLInputElement>) => {
        let value = evt.currentTarget.value;

        if (this.props.formatOnDigit) {
            switch (this.props.customFormat) {
                case "numberWithSeparators":
                    if (FormatUtility.numberFromNumberWithSeparators(this.props.value.toString()) === 0) {
                        evt.currentTarget.value = "";
                    }
                    break;
            }
        }

        if (this.props.onFocusHandler != null) {
            this.props.onFocusHandler(this.props.name, value);
        }
    };

    private onKeyPressHandler = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (this.props.formatOnDigit) {
            let key: string = evt.key;
            let cursorPos: number = this.inputRef.current?.selectionStart ?? 0;
            let value: string = evt.currentTarget.value;

            switch (this.props.customFormat) {
                case "numberWithSeparators":
                    let char = "";
                    let next2Chars = value.substring(cursorPos, cursorPos + 2);
                    let isCommaBefore = value.substring(0, cursorPos).includes(",");
                    // caratteri in sola lettura
                    let readonlyChars = [",", "."];
                    // pulsanti che si possono premere posizionandosi dopo i decimali (se presenti)
                    let lastPosEnabledKeys = ["Backspace", "ArrowLeft", "ArrowUp", "Home", "Enter", "Tab"];

                    switch (key) {
                        case "Backspace":
                            char = value.substring(cursorPos - 1, cursorPos);
                            if (readonlyChars.includes(char)) {
                                evt.preventDefault();
                                cursorPos--;
                            }
                            break;
                        case "Delete":
                            char = value.substring(cursorPos, cursorPos + 1);
                            if (readonlyChars.includes(char) || next2Chars === "0,") {
                                evt.preventDefault();
                                cursorPos++;
                            } else if (isCommaBefore) {
                                cursorPos++;
                            }
                            break;
                        case ",":
                        case ".":
                            char = value.substring(cursorPos, cursorPos + 1);
                            if (readonlyChars.includes(char)) {
                                cursorPos++;
                            }
                            evt.preventDefault();
                            break;
                    }

                    // impedisce la digitazione oltre la lunghezza dei decimali (se presenti)
                    if (
                        value.length === cursorPos &&
                        value.includes(",") === true &&
                        lastPosEnabledKeys.includes(key) === false
                    ) {
                        evt.preventDefault();
                    }

                    break;
            }

            if (cursorPos !== this.inputRef.current?.selectionStart) {
                if (this.inputRef.current) {
                    this.inputRef.current.selectionStart = cursorPos;
                    this.inputRef.current.selectionEnd = cursorPos;
                }
            }
        }
    };

    private onChangeHandler = (evt: React.ChangeEvent<HTMLInputElement>) => {
        let value = evt.currentTarget.value;

        if (this.props.formatOnDigit) {
            let cursorPos = this.inputRef.current?.selectionStart ?? 0;
            let numCaratteriNonConsentiti = 0;
            let regex: RegExp;
            switch (this.props.customFormat) {
                case "numberWithSeparators":
                    regex = /[^0-9,.]/g;
                    numCaratteriNonConsentiti = (value.substring(0, cursorPos).match(regex) || []).length;
                    cursorPos = cursorPos - numCaratteriNonConsentiti;
                    value = value.replace(regex, "");

                    let zeroAtBeginning = (value.substring(0, cursorPos).match(/0+/) || [""])[0].length;
                    if (zeroAtBeginning === value.substring(0, cursorPos).length) {
                        cursorPos = cursorPos - zeroAtBeginning;
                    }

                    let cursorPosBefore = (value.substring(0, cursorPos).match(/[.,]/g) || []).length;
                    let hasCommaBefore = (value.substring(0, cursorPos).match(/[,]/g) || []).length === 1;
                    value = FormatUtility.numberWithSeparators(value, this.props.step ?? 0);
                    let cursorPosAfter = (value.substring(0, cursorPos).match(/[.,]/g) || []).length;
                    let hasCommaAfter = (value.substring(0, cursorPos).match(/[,]/g) || []).length === 1;
                    if (hasCommaBefore === false && hasCommaAfter === true) {
                        cursorPosAfter--;
                    }

                    cursorPos = cursorPos + (cursorPosAfter - cursorPosBefore);
                    break;
                case "noNumbers":
                    regex = /[0-9]/g;
                    numCaratteriNonConsentiti = (value.substring(0, cursorPos).match(regex) || []).length;
                    value = value.replace(regex, "");
                    cursorPos = cursorPos - numCaratteriNonConsentiti;
                    break;
                case "accented":
                    regex = /[^a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]/g;
                    numCaratteriNonConsentiti = (value.substring(0, cursorPos).match(regex) || []).length;
                    value = value.replace(regex, "");
                    cursorPos = cursorPos - numCaratteriNonConsentiti;
                    break;
                case "uppercaseAndNumbers":
                    regex = /[^a-zA-Z0-9]/g;
                    numCaratteriNonConsentiti = (value.substring(0, cursorPos).match(regex) || []).length;
                    value = value.replace(regex, "");
                    cursorPos = cursorPos - numCaratteriNonConsentiti;
                    value = value.toUpperCase();
                    break;
                case "numbers":
                    regex = /[^0-9]/g;
                    numCaratteriNonConsentiti = (value.substring(0, cursorPos).match(regex) || []).length;
                    value = value.replace(regex, "");
                    cursorPos = cursorPos - numCaratteriNonConsentiti;
                    break;
                case "streetNumber":
                    regex = /[^0-9A-Za-zàèìòù\s/.]/g;
                    numCaratteriNonConsentiti = (value.substring(0, cursorPos).match(regex) || []).length;
                    value = value.replace(regex, "");
                    cursorPos = cursorPos - numCaratteriNonConsentiti;
                    break;
                case "noSpecials":
                    regex = /[^a-zA-Z0-9àáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,'-]/g;
                    numCaratteriNonConsentiti = (value.substring(0, cursorPos).match(regex) || []).length;
                    value = value.replace(regex, "");
                    cursorPos = cursorPos - numCaratteriNonConsentiti;
                    break;
                case "phoneNumber":
                    regex = /[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð .!@#$%&*()="`~^_,'-]/g;
                    numCaratteriNonConsentiti = (value.substring(0, cursorPos).match(regex) || []).length;
                    value = value.replace(regex, "");
                    cursorPos = cursorPos - numCaratteriNonConsentiti;
                    break;
            }
            if (this.hasUnmounted === false) {
                this.setState({
                    cursorPos: cursorPos,
                });
            }
        }

        if (this.props.onChangeHandler != null) {
            this.props.onChangeHandler(this.props.name, value);
        }
    };

    private onBlurHandler = (evt: React.FocusEvent<HTMLInputElement>) => {
        let value = evt.currentTarget.value;

        switch (this.props.customFormat) {
            case "numberWithSeparators":
                let validRange = true;
                let valoreNumerico = FormatUtility.numberFromNumberWithSeparators(value) ?? 0;
                if (this.props.min && typeof this.props.min === "number") {
                    if (this.props.min > valoreNumerico) {
                        valoreNumerico = this.props.min;
                        value = FormatUtility.numberWithSeparators(valoreNumerico, this.props.step ?? 0);
                        validRange = false;
                    }
                }
                if (this.props.max && typeof this.props.max === "number") {
                    if (this.props.max < valoreNumerico) {
                        valoreNumerico = this.props.max;
                        value = FormatUtility.numberWithSeparators(valoreNumerico, this.props.step ?? 0);
                        validRange = false;
                    }
                }
                if (validRange === false) {
                    if (this.props.onChangeHandler != null) {
                        this.props.onChangeHandler(this.props.name, value);
                    }
                }
                break;
        }

        if (this.props.onBlurHandler != null) {
            this.props.onBlurHandler(this.props.name, value);
        }
    };

    public render(): JSX.Element {
        const {
            controlId,
            id,
            label,
            prepend,
            append,
            helper,
            className,
            type,
            name,
            placeholder,
            min,
            max,
            minLength,
            maxLength,
            step,
            value,
            required,
            infoCode,
            isInvalid,
            error,
            disabled,
            floatingLabel,
            fullWidth,
            inputClassName,
        } = this.props;

        const { inputType, autocomplete } = this.state;

        const getValue = (): string => {
            if (value == null || value === "") {
                return "";
            }
            switch (this.props.customFormat) {
                case "numberWithSeparators":
                    let valueFormatted = FormatUtility.numberWithSeparators(value.toString(), this.props.step ?? 0);
                    if (FormatUtility.numberFromNumberWithSeparators(valueFormatted) === 0) {
                        return "0,00";
                    }
                    return valueFormatted;
            }
            return value.toString();
        };

        const changeType = () => {
            if (type === "password") {
                if (this.state.inputType === "password") {
                    if (this.hasUnmounted === false) {
                        this.setState({
                            inputType: "text",
                        });
                    }
                } else {
                    if (this.hasUnmounted === false) {
                        this.setState({
                            inputType: "password",
                        });
                    }
                }
            }
        };

        return (
            <Form.Group className={fullWidth ? "mb-0 w-100" : "mb-0"} controlId={controlId}>
                {floatingLabel === false && label ? (
                    <Form.Label>
                        {label}
                        {required ? <RequiredIcon /> : null}
                        {infoCode ? <InfoIcon label={label} code={infoCode} /> : null}
                    </Form.Label>
                ) : null}
                <InputGroup
                    className={[
                        "rounded ccq-shadow",
                        floatingLabel !== false ? "floating-label-container" : "",
                        inputClassName || "",
                    ].join(" ")}
                >
                    {prepend ? (
                        <InputGroup.Prepend>
                            <InputGroup.Text className={["rounded-left", disabled ? "" : "bg-white"].join(" ")}>
                                {prepend}
                            </InputGroup.Text>
                        </InputGroup.Prepend>
                    ) : null}
                    <Form.Control
                        ref={this.inputRef}
                        id={id}
                        className={[
                            prepend == null ? "rounded-left" : "",
                            append == null && (type !== "password" || getValue().length === 0) ? "rounded-right" : "",
                            className ? className : "",
                        ].join(" ")}
                        type={inputType}
                        name={name}
                        value={getValue()}
                        onFocus={this.onFocusHandler}
                        onChange={this.onChangeHandler}
                        onBlur={this.onBlurHandler}
                        onKeyDown={this.onKeyPressHandler}
                        isInvalid={isInvalid}
                        custom={type === "range"}
                        min={min instanceof Date ? moment(min).format(DateFormatEnum.ISO_DATE) : min}
                        max={max instanceof Date ? moment(max).format(DateFormatEnum.ISO_DATE) : max}
                        step={step}
                        minLength={minLength}
                        maxLength={maxLength}
                        placeholder={floatingLabel === false ? placeholder : placeholder || " "}
                        autoComplete={autocomplete}
                        disabled={disabled}
                    />
                    {floatingLabel !== false && label ? (
                        <Form.Label>
                            {label}
                            {required ? <RequiredIcon /> : null}
                            {infoCode ? <InfoIcon label={label} code={infoCode} /> : null}
                        </Form.Label>
                    ) : null}
                    {type === "password" && getValue().length > 0 ? (
                        <InputGroup.Append>
                            <InputGroup.Text className="rounded-right bg-white cursor-pointer" onClick={changeType}>
                                {inputType === "password" ? (
                                    <BaseIcon icon={IconNameEnum.Nascondi} color={"default"} />
                                ) : (
                                    <BaseIcon icon={IconNameEnum.Mostra} color={"default"} />
                                )}
                            </InputGroup.Text>
                        </InputGroup.Append>
                    ) : null}
                    {append ? (
                        <InputGroup.Append>
                            <InputGroup.Text className={["rounded-right", disabled ? "" : "bg-white"].join(" ")}>
                                {append}
                            </InputGroup.Text>
                        </InputGroup.Append>
                    ) : null}
                    {helper ? (
                        <Form.Text muted className="p-2 mt-0">
                            {helper}
                        </Form.Text>
                    ) : null}
                </InputGroup>
                <Form.Control.Feedback type="invalid" className="d-block bg-transparent mt-1">
                    {error}
                </Form.Control.Feedback>
            </Form.Group>
        );
    }
}

export { BaseInput as Unconnected };
export default BaseInput;
